import React from 'react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-shell via-sand-light to-fog">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 mr-3">
                <img 
                  src="/images/HLVLogo.svg" 
                  alt="Harmony Living by V Logo" 
                  className="w-full h-full"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-coastal-blue font-serif">Harmony Living by V</h1>
                <p className="text-sm text-gray-600">Privacy Policy</p>
              </div>
            </div>
            <a 
              href="/"
              className="bg-coastal-blue text-white px-4 py-2 rounded-full hover:bg-coastal-dark transition-colors font-medium"
            >
              Back to Home
            </a>
          </div>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="prose max-w-none">
            <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">Privacy Policy</h1>
            <p className="text-gray-600 mb-8 text-center">
              <strong>Effective Date:</strong> December 1, 2024 | <strong>Last Updated:</strong> January 8, 2025
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Introduction</h2>
              <p className="mb-4">
                Welcome to Harmony Living by V ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our professional organizing services.
              </p>
              <p>
                By using our services, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Information We Collect</h2>
              <p className="mb-4">
                We may collect and process the following types of information:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-700 mb-3">Personal Information:</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Name, email address, and phone number</li>
                <li>Home address and service location</li>
                <li>Information about your organizing needs and preferences</li>
                <li>Communication records and correspondence</li>
                <li>Payment information (processed securely through third-party providers)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mb-3">Automatically Collected Information:</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>IP address and device information</li>
                <li>Browser type and operating system</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website information</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. How We Use Your Information</h2>
              <p className="mb-4">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Providing and improving our professional organizing services</li>
                <li>Communicating with you about appointments, services, and updates</li>
                <li>Processing payments and managing billing</li>
                <li>Customizing your service experience</li>
                <li>Sending marketing communications (with your consent)</li>
                <li>Analyzing website usage and improving our online presence</li>
                <li>Complying with legal obligations and protecting our rights</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 mb-6">
                <li>With your explicit consent</li>
                <li>To trusted service providers who assist in our operations (under strict confidentiality agreements)</li>
                <li>To comply with legal obligations or respond to legal requests</li>
                <li>To protect our rights, property, or safety, or that of our clients</li>
                <li>In connection with a business transfer or merger (with notice to affected users)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mb-3">4.1 SMS Clause</h3>
              <div className="bg-coastal-blue/5 p-4 rounded-lg border-l-4 border-coastal-blue">
                <p className="text-gray-700">
                  All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Data Security</h2>
              <p className="mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication measures</li>
                <li>Employee training on data protection practices</li>
                <li>Secure disposal of information when no longer needed</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Cookies and Tracking Technologies</h2>
              <p className="mb-4">
                Our website uses cookies and similar technologies to enhance your browsing experience. You can control cookie settings through your browser preferences. Types of cookies we use include:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Essential Cookies:</strong> Necessary for website functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand website usage patterns</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Your Rights and Choices</h2>
              <p className="mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Access to your personal information</li>
                <li>Correction of inaccurate or incomplete information</li>
                <li>Deletion of your personal information (subject to legal and business requirements)</li>
                <li>Restriction of processing in certain circumstances</li>
                <li>Data portability rights</li>
                <li>Withdrawal of consent for marketing communications</li>
              </ul>
              <p className="mb-4">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Data Retention</h2>
              <p>
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Generally, we retain client information for up to 7 years for business and legal purposes, after which it is securely deleted.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibent text-gray-800 mb-4">9. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. This Privacy Policy does not apply to those external sites. We encourage you to review the privacy policies of any third-party websites you visit.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date. Your continued use of our services constitutes acceptance of the revised policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Contact Information</h2>
              <p className="mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-sand-light p-4 rounded-lg">
                <p><strong>Harmony Living by V</strong></p>
                <p>Email: <a href="mailto:v@harmonylivingbyv.com" className="text-coastal-blue hover:underline">v@harmonylivingbyv.com</a></p>
                <p>Phone: Local: <a href="tel:+13103883970" className="text-coastal-blue hover:underline">(310) 388-3970</a> | Toll Free: <a href="tel:+18668843970" className="text-coastal-blue hover:underline">(866) 884-3970</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;